import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from 'react';
import { fetchUserPermissions } from '@utils/fetchPermissions';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setLoading } from '@store/reducers/auth/loginSlice';

interface PermissionsContextType {
    permissions: string[];
    loading: boolean;
    fetchPermissions: () => Promise<void>;
}

interface PermissionsProviderProps {
    children: ReactNode;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
    undefined
);

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
    children,
}) => {
    const [permissions, setPermissions] = useState<string[]>([]);
    const dispatch = useAppDispatch();
    const [loading, setLoadingState] = useState<boolean>(true);
    const isAuthenticated = useAppSelector(
        (state) => state.login.isAuthenticated
    );

    const fetchPermissions = async () => {
        try {
            const perms = await fetchUserPermissions();

            if (
                perms &&
                perms.length > 0 &&
                perms.every((perm) => typeof perm === 'string')
            ) {
                setPermissions(perms as string[]);
            } else {
                setPermissions([]);
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setPermissions([]);
        } finally {
            setLoadingState(false);
            dispatch(setLoading(false));
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchPermissions();
        }
    }, [isAuthenticated, dispatch]);

    return (
        <PermissionsContext.Provider
            value={{ permissions, loading, fetchPermissions }}
        >
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionsContext);

    if (!context) {
        throw new Error(
            'usePermissions must be used within a PermissionsProvider'
        );
    }
    return context;
};
